<h2 mat-dialog-title>
  <ng-container *ngIf="isNewGoogleAdPost; else editTitle">
    {{ "brand.campaign.content.googleAdUploadCreateGoogleAd" | translate }}
  </ng-container>
  <ng-template #editTitle>
    {{ "brand.campaign.content.googleAdUploadEditGoogleAd" | translate }}
  </ng-template>
</h2>
<mat-dialog-content>
  <form>
    <section class="upload-image">
      <h3>
        {{ "brand.campaign.content.googleAdAddImages" | translate }}
        <app-tooltip
          text="{{
            'brand.campaign.content.googleAdAddImagesToolTip' | translate
          }}"
        ></app-tooltip>
      </h3>

      <ul *ngIf="googleAdPost.images.length > 0" class="slider-list">
        <ng-container>
          <li *ngFor="let image of googleAdPost.images" class="slider-item">
            <div class="slider-item-button">
              <img class="slider-item-img" [src]="image.url" />
            </div>
            <span class="slider-item-details">
              <button class="remove" (click)="deleteFileConfirm(image)">
                <mat-icon *ngIf="!deletingFileId || deletingFileId !== image.id"
                  >delete</mat-icon
                >
                <mat-spinner
                  *ngIf="deletingFileId === image.id"
                  [diameter]="25"
                ></mat-spinner>
              </button>
            </span>
          </li>
        </ng-container>
      </ul>

      <div
        class="issue-message issue-message--error"
        *ngIf="!hasRequiredImages"
      >
        {{
          "brand.campaign.content.googleAdAddRequiredImagesError" | translate
        }}
      </div>
      <div
        class="issue-message issue-message--warning"
        *ngIf="hasReachedImagesLimit"
      >
        {{
          "brand.campaign.content.googleAdMaxImagesError"
            | translate: { maxImages: maxImages }
        }}
      </div>

      <div class="upload-image-error-message" *ngIf="showInvalidImageMessage">
        {{
          "brand.campaign.content.googleAdAddImagesInvalidSizeError" | translate
        }}
      </div>

      <app-file-uploader
        [ngClass]="{ disabled: hasReachedImagesLimit }"
        [uploaderTitle]="
          'brand.campaign.content.googleAdUploadImagesTitle' | translate
        "
        [uploaderSubtitle]="
          'brand.campaign.content.googleAdUploadImagesSubtitle' | translate
        "
        [uploadUrl]="uploaderUrl"
        [uploadData]="uploadData"
        (apiResponse)="handleUploadResponse($event)"
        (uploadError)="fileUploaderErrorHandler($event)"
        [responseMapper]="fileUploaderResponseMapper"
      />
    </section>
  </form>
  <section class="suggested-text">
    <form [formGroup]="form">
      <h3>
        {{ "googleAdEditor.googleAdUploadHeadlines" | translate }}
        <app-tooltip
          text="{{
            'googleAdEditor.googleAdUploadHeadlinesToolTip' | translate
          }}"
        ></app-tooltip>
      </h3>
      <ng-container
        *ngFor="let h of headlines; let i = index"
        [ngTemplateOutlet]="headlineField"
        [ngTemplateOutletContext]="{ index: i }"
      >
      </ng-container>
      <div
        class="add-link"
        *ngIf="showHeadlineIndex < 5"
        (click)="showNextHeadline()"
      >
        {{ "googleAdEditor.googleAdAddHeadline" | translate }}
      </div>

      <h3>
        {{ "googleAdEditor.googleAdUploadLongHeadline" | translate }}
        <app-tooltip
          text="{{
            'googleAdEditor.googleAdUploadLongHeadlineToolTip' | translate
          }}"
        ></app-tooltip>
      </h3>
      <mat-form-field class="mat-form-field--multi-error">
        <input
          matInput
          placeholder="{{
            'googleAdEditor.googleAdUploadLongHeadline' | translate
          }}"
          maxlength="90"
          type="text"
          formControlName="longHeadline"
        />
        <mat-hint align="end">
          {{ googleAdPost.longHeadline?.length || 0 }} / 90
        </mat-hint>
        <mat-error>
          <ng-container
            [ngTemplateOutlet]="fieldErrorMessages"
            [ngTemplateOutletContext]="{
              formControl: form.controls.longHeadline
            }"
          >
          </ng-container>
        </mat-error>
      </mat-form-field>

      <h3>
        {{ "googleAdEditor.googleAdUploadDescriptions" | translate }}
        <app-tooltip
          text="{{
            'googleAdEditor.googleAdUploadDescriptionsToolTip' | translate
          }}"
        ></app-tooltip>
      </h3>
      <ng-container
        *ngFor="let d of descriptions; let i = index"
        [ngTemplateOutlet]="descriptionField"
        [ngTemplateOutletContext]="{ index: i }"
      >
      </ng-container>
      <div
        class="add-link"
        *ngIf="showDescriptionIndex < 5"
        (click)="showNextDescription()"
      >
        {{ "googleAdEditor.googleAdAddDescription" | translate }}
      </div>
    </form>
  </section>
</mat-dialog-content>
<mat-dialog-actions>
  <button class="btn btn--transparent" tabindex="-1" [mat-dialog-close]="">
    {{ "shared.cancel" | translate }}
  </button>
  <button
    class="btn btn--primary"
    tabindex="-1"
    (click)="saveContent()"
    [disabled]="isSaveDisabled()"
  >
    <span *ngIf="saving">{{ "shared.saving" | translate }}</span>
    <span *ngIf="!saving">{{ "shared.save" | translate }}</span>
  </button>
</mat-dialog-actions>

<ng-template #fieldErrorMessages let-formControl="formControl">
  <mat-error *ngIf="formControl.hasError('required')">
    {{ "shared.requiredField" | translate }}
  </mat-error>

  <mat-error *ngIf="formControl.hasError('forbiddenChars')">{{
    getForbiddenCharErrorMessage()
  }}</mat-error>

  <mat-error *ngIf="formControl.hasError('twoConsecutiveDots')">
    {{ "shared.errorTwoDotsPattern" | translate }}
  </mat-error>

  <mat-error *ngIf="formControl.hasError('includeEmojis')"
    >{{ "brand.campaign.content.googleAdEmojiCharError" | translate }}
  </mat-error>

  <mat-error *ngIf="formControl.hasError('noUrlProtocol')">
    {{ "brand.campaign.content.googleAdUrlCharError" | translate }}
  </mat-error>

  <mat-error *ngIf="formControl.hasError('uppercaseLimit')">
    {{ "brand.campaign.content.googleAdCapitalizeWarning" | translate }}
  </mat-error>
</ng-template>

<ng-template #headlineField let-index="index">
  <mat-form-field
    class="mat-form-field--multi-error"
    *ngIf="showHeadlineIndex - 1 >= index"
  >
    <input
      matInput
      placeholder="{{ 'googleAdEditor.googleAdUploadHeadline' | translate }}"
      maxlength="30"
      type="text"
      [formControl]="headlines[index]"
    />
    <mat-hint align="end">
      {{ headlines[index].value.length || 0 }} / 30
    </mat-hint>
    <mat-error>
      <ng-container
        [ngTemplateOutlet]="fieldErrorMessages"
        [ngTemplateOutletContext]="{ formControl: headlines[index] }"
      >
      </ng-container>
    </mat-error>
  </mat-form-field>
</ng-template>

<ng-template #descriptionField let-index="index">
  <mat-form-field
    class="mat-form-field--multi-error"
    *ngIf="showDescriptionIndex - 1 >= index"
  >
    <input
      matInput
      placeholder="{{ 'googleAdEditor.googleAdUploadDescription' | translate }}"
      maxlength="90"
      type="text"
      [formControl]="descriptions[index]"
    />
    <mat-hint align="end">
      {{ descriptions[index].value.length || 0 }} / 90
    </mat-hint>
    <mat-error>
      <ng-container
        [ngTemplateOutlet]="fieldErrorMessages"
        [ngTemplateOutletContext]="{ formControl: descriptions[index] }"
      >
      </ng-container>
    </mat-error>
  </mat-form-field>
</ng-template>
